import { useState, useEffect } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import {
  ADMIN_SETTLES_BY_REQUEST_DATE,
  ADMIN_BULK_UPDATE_SETTLE_STATE,
  ADMIN_REQUEST_SETTLE,
  ADMIN_CALCULATE_PENDING_SETTLEMENT,
  formatKRW,
} from "./api/admin-settle";
import type {
  AdminSettlesByRequestDateVars,
  AdminSettlesByRequestDateData,
  AdminBulkUpdateSettleStateVars,
  AdminBulkUpdateSettleStateData,
  AdminRequestSettleVars,
  AdminRequestSettleData,
  AdminCalculatePendingSettlementVars,
  AdminCalculatePendingSettlementData,
} from "./model/types";
import { notifyError, notify } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import Modal from "../../../components/common/modal";

const STORE_SEARCH = gql`
  query AdminSettleManagementStoreSearch($keyword: String!) {
    storeSearch(keyword: $keyword, lat: null, lng: null) {
      id
      title
      seller {
        id
        name
        contactNumber
        businessNumber
      }
    }
  }
`;

export default function AdminSettleManagement() {
  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  const [requestDate, setRequestDate] = useState(today);
  const [stateFilter, setStateFilter] = useState<string>("요청");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // 수동 정산요청 관련 상태
  const [showManualSettleModal, setShowManualSettleModal] = useState(false);
  const [storeSearchKeyword, setStoreSearchKeyword] = useState("");
  const [debouncedStoreKeyword, setDebouncedStoreKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<any>(null);
  const [selectedSettleWay, setSelectedSettleWay] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { data, loading, refetch } = useQuery<
    AdminSettlesByRequestDateData,
    AdminSettlesByRequestDateVars
  >(ADMIN_SETTLES_BY_REQUEST_DATE, {
    variables: {
      requestDate,
      state: stateFilter === "전체" ? null : stateFilter,
    },
    onError: (error) => {
      notifyError(error.message || "정산요청 조회 중 오류가 발생했습니다.");
    },
  });

  const [bulkUpdate, { loading: updating }] = useMutation<
    AdminBulkUpdateSettleStateData,
    AdminBulkUpdateSettleStateVars
  >(ADMIN_BULK_UPDATE_SETTLE_STATE, {
    onCompleted: (data) => {
      if (data.adminBulkUpdateSettleState.ok) {
        notify("정산요청 상태가 변경되었습니다.");
        setSelectedIds(new Set());
        refetch();
      } else {
        notifyError(
          data.adminBulkUpdateSettleState.error ||
            "정산요청 상태변경 중 오류가 발생했습니다."
        );
      }
    },
    onError: (error) => {
      notifyError(error.message || "정산요청 상태변경 중 오류가 발생했습니다.");
    },
  });

  // 매장 검색
  const { data: storeSearchData } = useQuery(STORE_SEARCH, {
    variables: { keyword: debouncedStoreKeyword },
    skip: !debouncedStoreKeyword,
  });

  // 정산 예정 금액 조회
  const { data: settlementData, loading: settlementLoading } = useQuery<
    AdminCalculatePendingSettlementData,
    AdminCalculatePendingSettlementVars
  >(ADMIN_CALCULATE_PENDING_SETTLEMENT, {
    variables: { sellerId: selectedStore?.seller?.id || 0 },
    skip: !selectedStore?.seller?.id,
  });

  // 수동 정산요청 mutation
  const [adminRequestSettle, { loading: requestingSettle }] = useMutation<
    AdminRequestSettleData,
    AdminRequestSettleVars
  >(ADMIN_REQUEST_SETTLE, {
    onCompleted: (data) => {
      if (data.adminRequestSettle.ok) {
        notify("정산요청이 생성되었습니다.");
        setShowManualSettleModal(false);
        setShowConfirmModal(false);
        setSelectedStore(null);
        setStoreSearchKeyword("");
        setDebouncedStoreKeyword("");
        setSelectedSettleWay("");
        refetch();
      } else {
        notifyError(
          data.adminRequestSettle.error || "정산요청 생성 중 오류가 발생했습니다."
        );
      }
    },
    onError: (error) => {
      notifyError(error.message || "정산요청 생성 중 오류가 발생했습니다.");
    },
  });

  // 디바운스 처리
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStoreKeyword(storeSearchKeyword);
    }, 500);
    return () => clearTimeout(timer);
  }, [storeSearchKeyword]);

  const settles = data?.adminSettlesByRequestDate || [];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // 현재 필터된 목록에서 "요청" 상태인 것만 선택
      const requestSettles = settles.filter((s) => s.state === "요청");
      setSelectedIds(new Set(requestSettles.map((s) => s.settleId)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (settleId: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(settleId);
    } else {
      newSelected.delete(settleId);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkUpdate = async (nextState: "승인" | "거절") => {
    if (selectedIds.size === 0) {
      notifyError("선택된 항목이 없습니다.");
      return;
    }

    const confirmMessage =
      nextState === "승인"
        ? `선택한 ${selectedIds.size}개의 정산요청을 승인하시겠습니까?`
        : `선택한 ${selectedIds.size}개의 정산요청을 거절하시겠습니까?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    await bulkUpdate({
      variables: {
        settleIds: Array.from(selectedIds),
        nextState,
      },
    });
  };

  const isAllSelected =
    settles.filter((s) => s.state === "요청").length > 0 &&
    settles
      .filter((s) => s.state === "요청")
      .every((s) => selectedIds.has(s.settleId));

  const isIndeterminate =
    selectedIds.size > 0 &&
    selectedIds.size < settles.filter((s) => s.state === "요청").length;

  const handleManualSettleRequest = () => {
    if (!selectedStore?.seller?.id) {
      notifyError("매장을 선택해주세요.");
      return;
    }
    if (!selectedSettleWay) {
      notifyError("정산 방식을 선택해주세요.");
      return;
    }
    if (
      settlementData?.adminCalculatePendingSettlement.settlePrice === 0
    ) {
      notifyError("정산할 금액이 없습니다.");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmManualSettle = () => {
    if (selectedStore?.seller?.id && selectedSettleWay) {
      adminRequestSettle({
        variables: {
          sellerId: selectedStore.seller.id,
          settleWay: selectedSettleWay,
        },
      });
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">정산요청 관리</h1>
        <button
          onClick={() => setShowManualSettleModal(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          수동 정산요청
        </button>
      </div>

      {/* 필터 영역 */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              정산요청일
            </label>
            <input
              type="date"
              value={requestDate}
              onChange={(e) => setRequestDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              상태 필터
            </label>
            <select
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value);
                setSelectedIds(new Set());
              }}
              className="w-full p-2 border border-gray-300 rounded"
              disabled={loading}
            >
              <option value="전체">전체</option>
              <option value="요청">요청</option>
              <option value="승인">승인</option>
              <option value="거절">거절</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => refetch()}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? <LoadingSpinnerInline /> : "조회"}
            </button>
          </div>
        </div>
      </div>

      {/* 액션 버튼 영역 */}
      {selectedIds.size > 0 && (
        <div className="bg-yellow-50 p-4 rounded-lg shadow-md mb-6 border border-yellow-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {selectedIds.size}개 항목 선택됨
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkUpdate("승인")}
                disabled={updating}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updating ? (
                  <LoadingSpinnerInline />
                ) : (
                  <>
                    <span>승인 처리</span>
                  </>
                )}
              </button>
              <button
                onClick={() => handleBulkUpdate("거절")}
                disabled={updating}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updating ? (
                  <LoadingSpinnerInline />
                ) : (
                  <>
                    <span>거절 처리</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 테이블 영역 */}
      <div className="bg-white rounded-lg shadow-md">
        {loading ? (
          <div className="p-8 text-center">
            <LoadingSpinnerInline />
          </div>
        ) : settles.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            해당 날짜의 정산요청이 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 border-b text-left">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4"
                    />
                  </th>
                  <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    매장명
                  </th>
                  <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    정산금액
                  </th>
                  <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    정산방식
                  </th>
                  <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    요청일시
                  </th>
                  <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    상태
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {settles.map((settle) => {
                  const isRequested = settle.state === "요청";
                  const isSelected = selectedIds.has(settle.settleId);
                  return (
                    <tr
                      key={settle.settleId}
                      className={`hover:bg-gray-50 ${
                        isSelected ? "bg-blue-50" : ""
                      }`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) =>
                            handleSelectOne(settle.settleId, e.target.checked)
                          }
                          disabled={!isRequested}
                          className="w-4 h-4 disabled:opacity-50"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {settle.storeTitle || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {formatKRW(settle.settlePrice)}원
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {settle.settleWay || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {new Date(settle.requestDate).toLocaleString("ko-KR", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            settle.state === "승인"
                              ? "bg-green-100 text-green-800"
                              : settle.state === "거절"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {settle.state}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 수동 정산요청 모달 */}
      {showManualSettleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">수동 정산요청</h2>
                <button
                  onClick={() => {
                    setShowManualSettleModal(false);
                    setSelectedStore(null);
                    setStoreSearchKeyword("");
                    setDebouncedStoreKeyword("");
                    setSelectedSettleWay("");
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              {/* 매장 검색 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  매장 검색
                </label>
                <input
                  type="text"
                  value={storeSearchKeyword}
                  onChange={(e) => setStoreSearchKeyword(e.target.value)}
                  placeholder="매장명 또는 주소로 검색"
                  className="w-full p-2 border border-gray-300 rounded"
                />
                {storeSearchData?.storeSearch && (
                  <div className="mt-2 border border-gray-200 rounded max-h-40 overflow-y-auto">
                    {storeSearchData.storeSearch.map((store: any) => (
                      <div
                        key={store.id}
                        onClick={() => {
                          setSelectedStore(store);
                          setStoreSearchKeyword(store.title);
                        }}
                        className={`p-2 cursor-pointer hover:bg-gray-100 ${
                          selectedStore?.id === store.id ? "bg-blue-50" : ""
                        }`}
                      >
                        <div className="font-medium">{store.title}</div>
                        {store.seller && (
                          <div className="text-sm text-gray-500">
                            사장님: {store.seller.name || store.seller.contactNumber}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 선택된 매장 정보 */}
              {selectedStore && (
                <div className="mb-6 p-4 bg-gray-50 rounded">
                  <h3 className="font-semibold mb-2">선택된 매장</h3>
                  <div className="text-sm">
                    <div>매장명: {selectedStore.title}</div>
                    {selectedStore.seller && (
                      <>
                        <div>사장님 ID: {selectedStore.seller.id}</div>
                        <div>연락처: {selectedStore.seller.contactNumber}</div>
                        {selectedStore.seller.businessNumber && (
                          <div>사업자번호: {selectedStore.seller.businessNumber}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* 정산 예정 금액 */}
              {selectedStore?.seller?.id && (
                <div className="mb-6">
                  {settlementLoading ? (
                    <div className="text-center py-4">
                      <LoadingSpinnerInline />
                    </div>
                  ) : settlementData?.adminCalculatePendingSettlement ? (
                    <div className="p-4 bg-blue-50 rounded">
                      <h3 className="font-semibold mb-2">정산 예정 금액</h3>
                      <div className="space-y-1 text-sm">
                        <div>
                          총 판매 금액:{" "}
                          {formatKRW(
                            settlementData.adminCalculatePendingSettlement.totalPrice
                          )}
                          원
                        </div>
                        <div className="font-bold text-lg text-red-600">
                          정산 금액:{" "}
                          {formatKRW(
                            settlementData.adminCalculatePendingSettlement.settlePrice
                          )}
                          원
                        </div>
                        <div>
                          정산 기간:{" "}
                          {`${settlementData.adminCalculatePendingSettlement.settlementStartDate} ~ ${settlementData.adminCalculatePendingSettlement.settlementEndDate}`}
                        </div>
                        <div>
                          다음 지급 예정일:{" "}
                          {settlementData.adminCalculatePendingSettlement.nextPayoutDate}
                        </div>
                        <div>
                          정산 정보 입력 여부:{" "}
                          {settlementData.adminCalculatePendingSettlement
                            .hasRequiredSettlementInfo
                            ? "완료"
                            : "미완료"}
                        </div>
                        <div>
                          주문 수:{" "}
                          {settlementData.adminCalculatePendingSettlement.orderCount}개
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      정산 예정 금액을 불러올 수 없습니다.
                    </div>
                  )}
                </div>
              )}

              {/* 정산 방식 선택 */}
              {selectedStore && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    정산 방식
                  </label>
                  <select
                    value={selectedSettleWay}
                    onChange={(e) => setSelectedSettleWay(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  >
                    <option value="">선택하세요</option>
                    <option value="단말기">단말기</option>
                    <option value="팝빌">팝빌</option>
                    <option value="계산서 발행 불가능">계산서 발행 불가능</option>
                  </select>
                </div>
              )}

              {/* 요청 버튼 */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowManualSettleModal(false);
                    setSelectedStore(null);
                    setStoreSearchKeyword("");
                    setDebouncedStoreKeyword("");
                    setSelectedSettleWay("");
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={handleManualSettleRequest}
                  disabled={
                    !selectedStore ||
                    !selectedSettleWay ||
                    requestingSettle ||
                    settlementLoading
                  }
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {requestingSettle ? "요청 중..." : "정산요청"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 확인 모달 */}
      <Modal
        isOpen={showConfirmModal}
        title="정산요청을 생성하시겠습니까?"
        content={
          selectedStore && settlementData?.adminCalculatePendingSettlement ? (
            <div className="text-center text-gray-600 text-sm mb-4 px-4 whitespace-pre-line">
              {`매장: ${selectedStore.title}\n정산 방식: ${selectedSettleWay}\n정산 금액: ${formatKRW(settlementData.adminCalculatePendingSettlement.settlePrice)}원`}
            </div>
          ) : null
        }
        onYes={handleConfirmManualSettle}
        onClose={() => setShowConfirmModal(false)}
        yesText="확인"
        noText="취소"
      />
    </div>
  );
}
